import React, { useState } from 'react';
import './styles.scss';
import { DynamicSVG, Images } from '@lua-frontend/numu-ui';
const escapeHtml = (unsafe) => {
  if (!unsafe) return '';
  return String(unsafe)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');
};

const RichTextViewer = ({
  text,
  html,
  header,
  edit,
  defaultExpanded = true,
  /** When false, collapsed content is not clickable to expand (stays clamped only) */
  allowedOpen = true,
  /** Collapsed mode: limit lines (e.g. 3) or pass maxHeight (px) */
  collapsedLines,
  maxHeight,
  /** Collapsed mode: default expanded? (default false = start collapsed) */
  collapsedDefaultExpanded = false,
  /* ---------- Class overrides ---------- */
  wrapperClassName = '',
  className = '',
  headerClassName = '',
  bodyClassName = '',
  contentClassName = '',
  editContainerClassName = '',

  /* ---------- Style overrides ---------- */
  wrapperStyle = {},
  style = {},
  headerStyle = {},
  bodyStyle = {},
  contentStyle = {},
  editContainerStyle = {}
}) => {
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);
  const [isContentExpanded, setIsContentExpanded] = useState(collapsedDefaultExpanded);

  const contentHtml = html != null && html !== '' ? html : null;
  const plainText = text != null && text !== '' ? text : '';

  const contentToRender =
    contentHtml != null ? contentHtml : plainText ? escapeHtml(plainText) : '';

  // Collapsed mode: show limited lines/height, click to expand (when allowedOpen)
  const isCollapsedMode =
    (collapsedLines != null && collapsedLines > 0) || (maxHeight != null && maxHeight > 0);
  const lineHeightPx = 20;
  const paddingVerticalPx = 20;
  // Max height when clamped: use maxHeight prop if set, else derive from collapsedLines
  const collapsedHeightPx =
    maxHeight != null && maxHeight > 0
      ? maxHeight
      : collapsedLines != null && collapsedLines > 0
        ? collapsedLines * lineHeightPx + paddingVerticalPx
        : null;

  const contentBox = contentToRender ? (
    <div
      className={`text-viewer__content ${contentClassName}`.trim()}
      style={contentStyle}
      dangerouslySetInnerHTML={{ __html: contentToRender }}
    />
  ) : (
    <div className={`text-viewer__content ${contentClassName}`.trim()} style={contentStyle}>
      <span className="text-viewer__placeholder">No content</span>
    </div>
  );

  const editButton = edit ? (
    <div
      className={`text-viewer__edit-container ${editContainerClassName}`.trim()}
      style={editContainerStyle}
    >
      {edit}
    </div>
  ) : null;

  /* ========================= */
  /* ===== Collapsed Mode ==== */
  /* ===== (header jaisa: limited height/lines, click se expand) */
  /* ========================= */

  if (isCollapsedMode) {
    const showClamped = allowedOpen
      ? !isContentExpanded && collapsedHeightPx
      : !!collapsedHeightPx;
    const toggleExpanded = () => allowedOpen && setIsContentExpanded((prev) => !prev);

    const collapsedContentBox = contentToRender ? (
      <div
        className={`text-viewer__content ${allowedOpen ? 'text-viewer__content--collapsible ' : ''}${
          showClamped ? 'text-viewer__content--line-clamp text-viewer__content--collapsed' : 'text-viewer__content--expanded'
        } ${contentClassName}`.trim()}
        style={{
          ...contentStyle,
          ...(showClamped
            ? {
                WebkitLineClamp: collapsedLines || 3,
                lineClamp: collapsedLines || 3,
                maxHeight: collapsedHeightPx
              }
            : {})
        }}
        dangerouslySetInnerHTML={{ __html: contentToRender }}
        onClick={allowedOpen ? toggleExpanded : undefined}
        onKeyDown={
          allowedOpen
            ? (e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  toggleExpanded();
                }
              }
            : undefined
        }
        role={allowedOpen ? 'button' : undefined}
        tabIndex={allowedOpen ? 0 : undefined}
        aria-expanded={allowedOpen ? isContentExpanded : undefined}
      />
    ) : (
      <div className={`text-viewer__content ${contentClassName}`.trim()} style={contentStyle}>
        <span className="text-viewer__placeholder">No content</span>
      </div>
    );

    return (
      <div
        className={`text-viewer__wrapper ${wrapperClassName}`.trim()}
        style={{ width: '100%', ...wrapperStyle }}
      >
        <div
          className={`text-viewer text-viewer--collapsed ${className}`.trim()}
          style={{ width: '100%', ...style }}
        >
          <div className={`text-viewer__body text-viewer__body--collapsible ${bodyClassName}`.trim()} style={bodyStyle}>
            {collapsedContentBox}
          </div>
        </div>

        {editButton && (
          <div
            className={`text-viewer__edit-container ${editContainerClassName}`.trim()}
            style={editContainerStyle}
          >
            {editButton}
          </div>
        )}
      </div>
    );
  }

  /* ========================= */
  /* ===== Accordion Mode ==== */
  /* ========================= */

  if (header) {
    return (
      <div
        className={`text-viewer__wrapper ${wrapperClassName}`.trim()}
        style={{ width: '100%', ...wrapperStyle }}
      >
        <div
          className={`text-viewer text-viewer--accordion ${className}`.trim()}
          style={{ width: '100%', ...style }}
        >
          <div
            className={`text-viewer__header ${headerClassName}`.trim()}
            style={headerStyle}
            onClick={() => setIsExpanded((prev) => !prev)}
            role="button"
            tabIndex={0}
            aria-expanded={isExpanded}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                setIsExpanded((prev) => !prev);
              }
            }}
          >
            {header}

            <span
              className={`text-viewer__chevron ${
                isExpanded ? 'text-viewer__chevron--up' : 'text-viewer__chevron--down'
              }`}
              aria-hidden
            >
              <DynamicSVG style={{ width: '14px', height: '14px'}} svgPath={Images.chevronUpSvg} />
            </span>
          </div>

          {isExpanded && (
            <div className={`text-viewer__body ${bodyClassName}`.trim()} style={bodyStyle}>
              {contentBox}
            </div>
          )}
        </div>

        {editButton && (
          <div
            className={`text-viewer__edit-container ${editContainerClassName}`.trim()}
            style={editContainerStyle}
          >
            {editButton}
          </div>
        )}
      </div>
    );
  }

  /* ========================= */
  /* ===== Fixed Mode ======== */
  /* ========================= */

  return (
    <div
      className={`text-viewer__wrapper ${wrapperClassName}`.trim()}
      style={{ width: '100%', ...wrapperStyle }}
    >
      <div
        className={`text-viewer text-viewer--fixed ${className}`.trim()}
        style={{ width: '100%', ...style }}
      >
        {contentBox}
      </div>

      {editButton && (
        <div
          className={`text-viewer__edit-container ${editContainerClassName}`.trim()}
          style={editContainerStyle}
        >
          {editButton}
        </div>
      )}
    </div>
  );
};

export { RichTextViewer };
